import { useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Progress } from "design-system/atoms/Progress"
import { FileClock } from "lucide-react"
import { useCustomerImportStatusQuery } from "modules/customers/hooks/useCustomerImportStatusQuery"
import { useRunningCustomerImportsQuery } from "modules/customers/hooks/useRunningCustomerImportsQuery"
import { runningCustomerImportsQueryKey } from "modules/customers/lib/runningCustomerImportsQuery"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Route as CustomersListRoute } from "routes/_base/_auth/customers/list/route"

export function RunningImports() {
  const { data: runningImports = [] } = useRunningCustomerImportsQuery()

  if (runningImports.length === 0) return null

  return (
    <div className="flex flex-col divide-y rounded-lg border bg-background">
      {runningImports.map(({ id }) => (
        <RunningImport key={id} importId={id} />
      ))}
    </div>
  )
}

type RunningImportProps = {
  importId: string
}

function RunningImport({ importId }: RunningImportProps) {
  const { t } = useTranslation()
  const search = CustomersListRoute.useSearch()
  const queryClient = useQueryClient()
  const { data } = useCustomerImportStatusQuery(importId)
  const isFinished = data?.status === "COMPLETED" || data?.status === "FAILED"

  useEffect(() => {
    if (!isFinished) return

    queryClient.invalidateQueries({ queryKey: runningCustomerImportsQueryKey })
    queryClient.invalidateQueries({ queryKey: ["customers"] })
  }, [isFinished, queryClient])

  // The email reports the result, so finished imports just drop out of the list
  if (!data || isFinished) return null

  const percent =
    data.totalRows === 0 ? 0 : Math.round((data.processedCount / data.totalRows) * 100)

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4">
      <div className="flex flex-1 items-center gap-3">
        <FileClock className="size-4 shrink-0 text-muted-foreground" />
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-sm font-medium">{t("customers.imports.running.title")}</span>
          <span className="text-sm text-muted-foreground">
            {t("customers.imports.running.description", {
              processedCount: data.processedCount,
              count: data.totalRows,
            })}
          </span>
          <Progress value={percent} className="max-w-sm" />
        </div>
      </div>
      <Button asChild variant="outline">
        <Link to="/customers/list/import" search={{ ...search, importId }}>
          {t("customers.imports.running.view")}
        </Link>
      </Button>
    </div>
  )
}
